import {
  IAssessmentReferralModel,
  AssessmentReferralModel,
} from '@common/models';
import { http } from '@common/utils';
import { trackPromise } from 'react-promise-tracker';
import { ServiceHelper } from '../serviceHelper';
import { ServiceMethodOptions } from '../types';

export class AssessmentReferralService {
  private static baseUrl: string = '/api/assessmentreferral';

  static tags = {
    assessmentReferrals: 'assessmentReferrals',
    getAssessmentReferral: 'getAssessmentReferral',
    updateAssessmentReferral: 'updateAssessmentReferral',
    deleteAssessmentReferral: 'deleteAssessmentReferral',
  };

  /**
   * Get an Assessment Referral by Id
   * @param id AssessmentReferralId
   * @param options
   * @returns Returns requested Assessment Referral
   */
  static getAssessmentReferralById = (
    id: number,
    options?: ServiceMethodOptions,
  ): Promise<AssessmentReferralModel> => {
    return trackPromise(
      new Promise<AssessmentReferralModel>((resolve, reject) => {
        http(`${this.baseUrl}/${id}`)
          .get<IAssessmentReferralModel>()
          .then((response) => {
            ServiceHelper.showSuccess('Assessment Referral fetched', options);
            resolve(new AssessmentReferralModel(response));
          })
          .catch((err) => {
            ServiceHelper.showError(
              'Failed to get Assessment Referral.',
              options,
            );
            reject(err);
          });
      }),
      this.tags.getAssessmentReferral,
    );
  };

  /**
   * Updates Assessment Referral
   * @param data AssessmentReferralModel to update
   * @param options Service Method Options
   * @returns AssessmentReferralModel
   */
  static upsertAssessmentReferral = (
    data: IAssessmentReferralModel,
    options?: ServiceMethodOptions,
  ): Promise<AssessmentReferralModel> => {
    return trackPromise(
      new Promise<AssessmentReferralModel>((resolve, reject) => {
        http(`${this.baseUrl}`)
          .post<IAssessmentReferralModel>(data)
          .then((response) => {
            ServiceHelper.showSuccess('Assessment Referral updated', options);
            resolve(new AssessmentReferralModel(response));
          })
          .catch((err) => {
            ServiceHelper.showError(
              'Failed to update Assessment Referral.',
              options,
            );
            reject(err);
          });
      }),
      this.tags.updateAssessmentReferral,
    );
  };

  /**
   * Deletes Assessment Referral by Id
   * @param id Assessment Referral Id
   * @param options Service Method Options
   * @returns boolean on success
   */
  static deleteAssessmentReferral = (
    id: number,
    options?: ServiceMethodOptions,
  ): Promise<boolean> => {
    return trackPromise(
      new Promise<boolean>((resolve, reject) => {
        http(`${this.baseUrl}/${id}`)
          .delete()
          .then(() => {
            ServiceHelper.showSuccess(
              'Assessment Referral has been deleted',
              options,
            );
            resolve(true);
          })
          .catch((err) => {
            ServiceHelper.showError(
              'Failed to delete Assessment Referral.',
              options,
            );
            reject(err);
          });
      }),
      this.tags.deleteAssessmentReferral,
    );
  };
}
